import React, { useEffect } from 'react';
import { Button, Typography, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { CheckCircleFilled } from '@ant-design/icons';
import { IconMessageCircle } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { getMyOrdersApi, getMyOrderDetailsApi } from "../../redux-toolkit/slices/orderCreamSlice";
import { updatePainLocationsForOrderDetails } from "../../redux-toolkit/slices/dashboardSlice";
import { formatDateMonthDayYear } from "../../helpers/date";
import { orderStatusEnums } from "../../constants/orderConstants";
import { ReactComponent as RxIcon } from "../../graphics/svgs/rx.svg";
import MiniBodyViewer from "../MiniBodyViewer";

const StageTwo = () => {
  // Render active order details
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orders, singleOrder } = useSelector((state: RootState) => state.orderCream);

  useEffect(() => {
    dispatch(getMyOrdersApi());
  }, [dispatch]);

  useEffect(() => {
    if (orders && orders.length > 0) {
      const latestOrder = orders[0];
      const orderId = latestOrder._id || latestOrder.id;
      if (orderId) {
        dispatch(getMyOrderDetailsApi(`orderId=${orderId}`));
      }
    }
  }, [dispatch, orders]);

  const latestOrder = orders && orders.length > 0 ? orders[0] : null;


  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section  Start */}
      <div className="mb-6 relative max-w-[800px] mx-auto w-full">
        <div className="flex justify-between items-start">
          <h2 className="text-2xl md:text-3xl font-bold mb-1 text-[var(--theme-text-primary)]" style={{ fontFamily: 'var(--font-h2-fontFamily)', fontWeight: 'var(--font-h2-fontWeight)' }}>Hi {latestOrder?.shippingDetails?.firstName}! Track your order status here.
          </h2>
          <Button 
            size="large"
            className="rounded-lg font-semibold shadow-none flex items-center gap-2 hover:!bg-[var(--primary-500)] hover:!text-white hover:!border-[var(--primary-500)] transition-colors duration-300"
            icon={<IconMessageCircle size={20} />}
            onClick={() => navigate('/chat')}
            style={{ 
              borderColor: 'var(--primary-500)',
              color: 'var(--primary-500)',
              backgroundColor: 'transparent',
              fontFamily: 'var(--font-btn-fontFamily)',
              height: '44px',
              padding: '0 24px',
              fontSize: '14px'
            }}
          >
            Message care team
          </Button>
        </div>
        <div className="flex flex-col gap-0">
                  <div className="mb-4">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <Typography 
                            className="h6 text-theme-text-secondary" 
                            style={{ 
                              border: '1px solid var(--neutral-600)',
                              padding: '6px 16px',
                              borderRadius: '9999px',
                              lineHeight: '1',
                              display: 'inline-block',
                              textAlign: 'left'
                            }}
                          >
                            Rx # {latestOrder?.processingDetails?.orderNumber} ordered on {latestOrder?.createdAt ? formatDateMonthDayYear(latestOrder.createdAt) : "December 12, 2025"}
                          </Typography>
                        </div>
                      </div>
        </div>
      </div>
      {/* Header Section  End */}
      
      {/* Stage information content start*/}
      <div 
        className="mx-auto rounded-3xl sm:rounded-[32px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 w-full overflow-hidden relative max-w-[1200px]" 
        style={{ backgroundColor: "var(--neutral-100)" }}
      >
        <div className="flex flex-row items-start gap-6 mb-8">
          <div 
            className="flex-shrink-0 overflow-hidden border-4 border-white shadow-md"
            style={{ width: '96px', height: '96px', borderRadius: '50%' }}
          >
            <img 
              src="https://i.pravatar.cc/150?u=dr_shareef" 
              alt="Dr. Shareef" 
              className="w-full h-full object-cover"
              style={{ borderRadius: '50%' }}
            />
          </div>
          <div className="flex flex-col justify-center pt-2">
          <h2 className="text-2xl md:text-3xl font-bold mb-1 text-[var(--theme-text-primary)]" style={{ fontFamily: 'var(--font-h2-fontFamily)', fontWeight: 'var(--font-h2-fontWeight)' }}> Show this container if stage PRESCRIBED : Your prescription has been prescribed.
          </h2>
            <p className="text-lg text-[var(--theme-text-secondary)] leading-relaxed mb-4 max-w-3xl" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
           Dr. Shareef prescribed you a compounded cream pain relief formula.
            </p>
          </div>
        </div>

        <Row gutter={[48, 24]} className="mt-8">
          {/* rx */}
          <Col xs={24} md={12}>
            <div className="flex flex-col gap-0">
                          <p className="text-base text-[var(--theme-text-secondary)] font-medium" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
                            Here is your prescription:  
            </p>

            {latestOrder?.processingDetails?.orderStatus !== orderStatusEnums.NEW && ( 
                         <div className="mb-4" style={{ 
                           border: '1px solid var(--neutral-600)', 
                           borderRadius: '12px', 
                           padding: '16px', 
                           display: 'inline-block', 
                           width: 'fit-content', 
                           maxWidth: '100%' 
                         }}> 
                           <h4 className="h6" style={{ color: 'var(--theme-text-secondary)', marginBottom: '1px', display: 'flex', alignItems: 'center', gap: '8px' }}> 
                             <div style={{ 
                               width: '24px', 
                               height: '24px', 
                               borderRadius: '50%', 
                               border: '1px solid var(--theme-text-secondary)', 
                               display: 'flex', 
                               alignItems: 'center', 
                               justifyContent: 'center' 
                             }}> 
                               <RxIcon style={{ width: '12px', height: '15px' }} /> 
                             </div> 
                             Custom formula for {latestOrder?.shippingDetails?.firstName} 
                           </h4> 
                           <div style={{ color: 'var(--theme-text-primary)' }}> 
                             {latestOrder?.processingDetails?.formulaAssigned !== null ? ( 
                               <> 
                                 {latestOrder?.processingDetails?.formulaAssigned?.activeIngredients?.split(',').map((ingredient: string, index: number) => ( 
                                   <p key={index} className="h5" style={{ marginBottom: index === 0 ? '1px' : '0' }}> 
                                     {ingredient.trim()} 
                                   </p> 
                                 ))} 
                                 <p className="h5" style={{ marginTop: '1px' }}> 
                                   {latestOrder?.processingDetails?.formulaAssigned?.creamBase} 
                                 </p> 
                               </> 
                             ) : ( 
                               <p className="h5">---</p> 
                             )} 
                           </div> 
                           
                           {/* Instructions */} 
                           {latestOrder?.processingDetails?.instructions && ( 
                             <div className="mt-3"> 
                               <p className="caption1" style={{ color: 'var(--theme-text-primary)' }}> 
                                 <span className="h6" style={{ color: 'var(--theme-text-secondary)' }}> 
                                   Directions for use:&nbsp; 
                                 </span> 
                                 {latestOrder?.processingDetails?.instructions} 
                               </p> 
                             </div> 
                           )} 
                           
                           {/* Other Instructions */} 
                           {latestOrder?.processingDetails?.otherInstructions && ( 
                             <div className="mt-3"> 
                               <p className="caption1" style={{ color: 'var(--theme-text-primary)' }}> 
                                 <span className="h6" style={{ color: 'var(--theme-text-secondary)' }}> 
                                   Directions for use:&nbsp; 
                                 </span> 
                                 {latestOrder?.processingDetails?.otherInstructions} 
                               </p> 
                             </div> 
                           )} 
                         </div> 
                       )}
            </div>
          </Col>
          
          {/* Pain Story & Location Visualization */}
          {singleOrder && (
            <Col xs={24} md={12}>
              <div className="header-content mb-6">
                <h4 className="h4 mb-2" style={{ textAlign: 'left' }}>Pain Story & Location</h4>
              </div>
              <div className="flex flex-col gap-3 w-full">
                <Row gutter={[24, 24]} align="top">
                  {/* Left Column: MiniBodyViewer */}
                  <Col xs={24} md={10} lg={8} className="flex justify-center">
                    <MiniBodyViewer isBodyClickEnable={false} painLocations={singleOrder?.painProfile?.painLocations || []} />
                  </Col>

                  {/* Right Column: Pain Story Content */}
                  <Col xs={24} md={14} lg={16}>
                    <div className="space-y-4">
                      {/* Pain Rating Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painRating === 0 ? (
                            <>Current pain level: 0/10 - No pain</>
                          ) : singleOrder.painProfile.painRating >= 1 && singleOrder.painProfile.painRating <= 3 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Mild pain</>
                          ) : singleOrder.painProfile.painRating >= 4 && singleOrder.painProfile.painRating <= 6 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Moderate pain</>
                          ) : singleOrder.painProfile.painRating >= 7 && singleOrder.painProfile.painRating <= 8 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Severe pain</>
                          ) : singleOrder.painProfile.painRating >= 9 && singleOrder.painProfile.painRating <= 10 ? (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10 - Extreme pain</>
                          ) : (
                            <>Current pain level: {singleOrder.painProfile.painRating}/10</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Duration Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painDuration === "Acute - less than 3 months" ? (
                            <>Pain started: Less than 3 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 3-6 months" ? (
                            <>Pain started: 3-6 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 6+ months" ? (
                            <>Pain started: Over 6 months ago</>
                          ) : singleOrder.painProfile.painDuration === "Chronic - 1+ years" ? (
                            <>Pain started: More than 1 year ago</>
                          ) : singleOrder.painProfile.painDuration === "acute" ? (
                            <>Pain started: Less than 3 months ago</>
                          ) : (
                            <>Pain started: More than 3 months ago</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Descriptions Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painDescriptions.length === 1 ? (
                            <>How it feels: {singleOrder.painProfile.painDescriptions[0]} sensation</>
                          ) : singleOrder.painProfile.painDescriptions.length > 1 ? (
                            <>How it feels: {singleOrder.painProfile.painDescriptions.slice(0, -1).join(", ")}, and {singleOrder.painProfile.painDescriptions[singleOrder.painProfile.painDescriptions.length - 1]} sensations</>
                          ) : (
                            <>How it feels: No specific descriptions provided</>
                          )}
                        </Typography>
                      </div>

                      {/* Pain Location Display */}
                      <div>
                        <Typography className="body1">
                          {singleOrder.painProfile.painLocations.length === 1 ? (
                            <>Pain location: {singleOrder.painProfile.painLocations[0]}</>
                          ) : singleOrder.painProfile.painLocations.length > 1 ? (
                            <>Pain locations: {singleOrder.painProfile.painLocations.join(", ")}</>
                          ) : (
                            <>Pain location: No specific locations provided</>
                          )}
                        </Typography>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          )}
        </Row>

        {/* What's Next Section */}
        <div className="mt-16 mb-10">
          <h4 className="text-xl font-bold mb-3 text-[var(--theme-text-primary)]" style={{ fontFamily: 'var(--font-h2-fontFamily)' }}>What's next?</h4>
          <p className="text-lg text-[var(--theme-text-secondary)] leading-relaxed max-w-4xl" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
          Within 24 hours your assessment will be reviewed and responded to.
          <br />
          After your prescription is issued, you will see information about the ingredients and how it targets your pain. During this time your cream will be compounded and shipped.
          </p>
        </div>

        {/* Placeholder for Full Assessment */}
        <div className="mt-8 pt-6 border-t border-[var(--neutral-200)] text-left">
            <span className="uppercase text-sm font-bold tracking-widest text-[var(--theme-text-secondary)] opacity-60" style={{ fontFamily: 'var(--font-body1-fontFamily)' }}>
                View Full Assessment
            </span>
        </div>

      </div>
      {/* Stage information content end*/}

    </div>
  );
};

export default StageTwo;
